import { faSquareInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip
} from '@mui/material';
import { useAtom } from 'jotai';
import { Controller, useFormContext } from 'react-hook-form';

import DynamicFormControl from '@/components/DynamicFormControl';
import { enableVulScanAtom } from '@/pages/Administration/Devices/Devices.store';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useSourceDialog } from '../SourceCreate.hooks';
import {
  selectedSourceDetailsAtom,
  selectedVulnFieldsAtom
} from '../SourceCreate.store';
import { type DynamicFieldData, SourceDialogMode } from '../SourceCreate.types';
import useDeviceVulnerabilityStyles from './DeviceVulnerability.styles';

export type FetchDeviceVulnerabilityProps = {
  vulSchema: DynamicFieldData[];
};
const FetchDeviceVulnerability = ({
  vulSchema
}: FetchDeviceVulnerabilityProps) => {
  const { classes } = useDeviceVulnerabilityStyles();

  // Form State
  const { control, formState, register } = useFormContext();

  const { errors } = formState;
  const [
    enableFetchDeviceVulnerabilities,
    setEnableFetchDeviceVulnerabilities
  ] = useAtom(enableVulScanAtom);

  const { mode, runIngestionMutation } = useSourceDialog();

  const [vulnFields, setVulnFields] = useAtom(selectedVulnFieldsAtom);
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);

  const handleFetchDeviceVulnerabilitiesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEnableFetchDeviceVulnerabilities(event.target.checked);
  };

  const handleVulnFieldsChange = (value: string | string[] | unknown[]) => {
    const selectedVulnFields = Array.isArray(value)
      ? (value as string[])
      : (value as string).split(',');
    setVulnFields(selectedVulnFields);
  };

  const handleOnDemandEventsIngestion = () => {
    runIngestionMutation.mutate({
      run_ingestion: true,
      source_uid: selectedSourceDetails?.source_uid || ''
    });
  };

  const renderVulnerabilityFields = () => {
    if (!(vulSchema?.[4]?.name && vulSchema[4]?.name === 'vuln_fields'))
      return null;

    const hasError = Boolean(errors[vulSchema[4]?.name]);
    const errorMessage = errors[vulSchema[4]?.name]?.message as string;

    const vulnFieldConfig: DynamicFieldData = {
      id: 0,
      name: vulSchema[4]?.name,
      type: 'autocomplete',
      label: vulSchema[4]?.label || 'Vulnerability Fields',
      value: vulnFields || [],
      validations: [],
      validation_type: '',
      options: vulSchema[4]?.options || [],
      showSelectAll: true,
      disabled: !enableFetchDeviceVulnerabilities,
      onFieldChange: handleVulnFieldsChange,
      config: {
        required: enableFetchDeviceVulnerabilities
      }
    };

    return (
      <Grid size={{ xs: 12, sm: 8, md: 8 }}>
        <InputLabel htmlFor="device-name-input">
          {vulSchema[4]?.label} <span> *</span>
        </InputLabel>
        <FormControl fullWidth>
          <DynamicFormControl {...vulnFieldConfig} />
          {hasError && <ErrorMessage message={errorMessage} />}
        </FormControl>
      </Grid>
    );
  };

  return (
    <>
      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <h2>
          {' '}
          <Controller
            control={control}
            name={vulSchema[0]?.name}
            defaultValue={false}
            render={({ field }) => (
              <InputLabel
                className={classes.fetchTimeLabel}
                htmlFor="device-name-input"
              >
                {vulSchema[0]?.label}
                <Switch
                  className={classes.fetchTimeSwitch}
                  defaultValue="false"
                  {...field}
                  checked={enableFetchDeviceVulnerabilities}
                  onChange={(value) => {
                    field.onChange(value);
                    handleFetchDeviceVulnerabilitiesChange(value);
                  }}
                  value={field.value}
                />
              </InputLabel>
            )}
          />
          <hr />
        </h2>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <InputLabel
          className={classes.fetchTimeLabel}
          htmlFor="device-name-input"
        >
          Fetch Polling Interval
        </InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={vulSchema[1]?.name}
            defaultValue="1 Day"
            rules={{ required: 'Polling Interval is required' }}
            render={({ field }) => (
              <Select
                disabled={!enableFetchDeviceVulnerabilities}
                {...field}
                onChange={field.onChange}
                value={field.value}
                sx={{ width: '100%' }}
              >
                {vulSchema[1]?.options?.map((item: string) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <InputLabel htmlFor="source_type_input">API Count *</InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={vulSchema[2]?.name}
            defaultValue="0"
            rules={{ required: 'API Count is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value}
                error={!!errors?.vuln_api_count}
                disabled={!enableFetchDeviceVulnerabilities}
                type="number"
                slotProps={{ htmlInput: { min: 1 } }}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <InputLabel htmlFor="source_type_input">Event Count</InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={vulSchema[3]?.name}
            defaultValue="0"
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value}
                error={!!errors?.vuln_event_count}
                disabled={!enableFetchDeviceVulnerabilities}
                type="number"
                slotProps={{ htmlInput: { min: 0 } }}
              />
            )}
          />
        </FormControl>
      </Grid>
      {mode === SourceDialogMode.Edit && (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputLabel htmlFor="source_type_input">
            On-demand Events Ingestion
          </InputLabel>
          <FormControl>
            <Box style={{ whiteSpace: 'nowrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOnDemandEventsIngestion}
                disabled={
                  selectedSourceDetails?.on_demand_vuln_scan ||
                  !enableFetchDeviceVulnerabilities
                }
              >
                {!runIngestionMutation.isPending && <span>Run Ingestion</span>}
                {runIngestionMutation.isPending && (
                  <CircularProgress color="secondary" size={20} />
                )}
              </Button>
              <TextField
                {...register('on_demand_vuln_scan')}
                name="on_demand_vuln_scan"
                value={selectedSourceDetails?.on_demand_vuln_scan || false}
                sx={{ display: 'none' }}
              />
              {selectedSourceDetails?.on_demand_vuln_scan && (
                <Tooltip
                  arrow
                  placement="right"
                  title="Previous request is in progress"
                >
                  <FontAwesomeIcon
                    style={{ marginLeft: '.5rem' }}
                    id="info-icon"
                    icon={faSquareInfo}
                    size="xl"
                  />
                </Tooltip>
              )}
            </Box>
          </FormControl>
        </Grid>
      )}
      {vulSchema?.[4]?.name &&
        vulSchema[4]?.name === 'vuln_fields' &&
        renderVulnerabilityFields()}
    </>
  );
};

export default FetchDeviceVulnerability;
